import type { ContactImportResultType } from "@/types/graphql/partner.generated";

/** Результат импорта без __typename: сумма по пачкам. */
export type ContactImportTotals = Omit<ContactImportResultType, "__typename">;

/**
 * Пределы импорта: пачка = лимит одного вызова importMarketingContacts на
 * бэкенде, max = строк за один импорт в кабинете, fileBytes = размер CSV.
 */
export const IMPORT_LIMITS = {
  chunk: 2000,
  max: 50_000,
  fileBytes: 10 * 1024 * 1024,
};

/** Режет строки на пачки для последовательных вызовов мутации. */
export function splitChunks<T>(rows: T[], size = IMPORT_LIMITS.chunk): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < rows.length; i += size) {
    chunks.push(rows.slice(i, i + size));
  }
  return chunks;
}

/** Складывает счётчики результатов пачек в один итог. */
export function sumResults(
  results: ContactImportTotals[]
): ContactImportTotals {
  return results.reduce<ContactImportTotals>(
    (total, result) => ({
      added: total.added + result.added,
      updated: total.updated + result.updated,
      invalid: total.invalid + result.invalid,
      duplicates: total.duplicates + result.duplicates,
      matchedViewers: total.matchedViewers + result.matchedViewers,
    }),
    { added: 0, updated: 0, invalid: 0, duplicates: 0, matchedViewers: 0 }
  );
}

/** Итог прогона пачек: сколько прошло, сумма и ошибка, на которой встали. */
export interface ChunksRun {
  done: number;
  total: number;
  totals: ContactImportTotals;
  error: unknown;
}

/**
 * Отправляет пачки строго по очереди. Ошибка пачки останавливает импорт:
 * уже загруженное остаётся, в итоге видно, сколько пачек прошло.
 */
export async function runChunks<T>(
  chunks: T[][],
  send: (chunk: T[]) => Promise<ContactImportTotals>,
  onProgress?: (done: number) => void
): Promise<ChunksRun> {
  const results: ContactImportTotals[] = [];
  for (const chunk of chunks) {
    try {
      // Пачки последовательны намеренно: бэкенд считает дубли и лимиты
      // по уже записанным строкам.
      results.push(await send(chunk));
    } catch (error) {
      return {
        done: results.length,
        total: chunks.length,
        totals: sumResults(results),
        error,
      };
    }
    onProgress?.(results.length);
  }
  return {
    done: results.length,
    total: chunks.length,
    totals: sumResults(results),
    error: null,
  };
}
